import React, { useState } from "react";
import { DataPSL } from "../../types/addServiceTypes";

type ServiceComponent = {
  component: string;
  componentName: string;
  formattedAvailabilityDate: string;
  configurations: {
    configuration: string;
    formattedDate: string;
  }[];
};

interface ServiceDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  pslData: DataPSL["ean"] | null;
  parseServiceComponentData: (pslData: DataPSL["ean"]) => ServiceComponent[];
}

export const ServiceDetailsModal: React.FC<ServiceDetailsModalProps> = ({
  isOpen,
  onClose,
  pslData,
  parseServiceComponentData,
}) => {
  const [expandedSections, setExpandedSections] = useState<Set<number>>(new Set());

  if (!isOpen || !pslData) return null;

  const serviceComponents = parseServiceComponentData(pslData);

  const toggleSection = (index: number) => {
    setExpandedSections((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(index)) {
        newSet.delete(index);
      } else {
        newSet.add(index);
      }
      return newSet;
    });
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} />

        <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full sm:p-6">
          <div className="absolute top-0 right-0 pt-4 pr-4">
            <button
              type="button"
              className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none transition-colors"
              onClick={onClose}
            >
              <span className="sr-only">Close</span>
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="sm:flex sm:items-start">
            <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
              <h3 className="text-lg leading-6 font-bold text-primary-600 mb-4">Service Catalogue</h3>
              <div className="mt-4">
                <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                  <p className="text-sm text-primary-500">
                    <span className="font-semibold text-primary-600">EAN:</span> {pslData.gsrn}
                  </p>
                  <p className="text-sm text-primary-500">
                    <span className="font-semibold text-primary-600">Type:</span> {pslData.typeOfMeter}
                  </p>
                  <p className="text-sm text-primary-500">
                    <span className="font-semibold text-primary-600">DGO:</span> {pslData.dgo}
                  </p>
                  <p className="text-sm text-primary-500">
                    <span className="font-semibold text-primary-600">Physical Status:</span>
                    {pslData.physicalStatusType}
                  </p>
                  {pslData.runningProcessStartDate && (
                    <p className="text-sm text-primary-500">
                      <span className="font-semibold text-primary-600">Running Process Date:</span>
                      {pslData.runningProcessStartDate}
                    </p>
                  )}
                </div>

                {serviceComponents.length > 0 ? (
                  <div className="space-y-4">
                    {serviceComponents.map((sc, index) => {
                      const isExpanded = expandedSections.has(index);

                      return (
                        <div
                          key={index}
                          className="border border-gray-200 rounded-lg overflow-hidden hover:border-primary-300 transition-colors"
                        >
                          <button
                            onClick={() => toggleSection(index)}
                            className="w-full p-4 text-left hover:bg-gray-50 transition-colors focus:outline-none focus:bg-gray-50"
                          >
                            <div className="flex items-center justify-between">
                              <div>
                                <h4 className="font-semibold text-primary-600 text-base">
                                  {sc.component} - {sc.componentName}
                                </h4>
                                <p className="text-sm text-primary-500 mt-1">
                                  <span className="font-medium">Service Available From:</span>{" "}
                                  {sc.formattedAvailabilityDate}
                                </p>
                              </div>
                              <div className="ml-4">
                                <svg
                                  className={`w-5 h-5 text-primary-600 transition-transform duration-200 ${
                                    isExpanded ? "rotate-180" : ""
                                  }`}
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 9l-7 7-7-7"
                                  />
                                </svg>
                              </div>
                            </div>
                          </button>

                          {isExpanded && sc.configurations.length > 0 && (
                            <div className="border-t border-gray-200 p-4 ">
                              <p className="text-sm font-semibold text-primary-600 mb-3">Available Configurations:</p>
                              <div className="overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200">
                                  <thead className="bg-white">
                                    <tr>
                                      <th className="px-3 py-2 text-left text-xs font-medium text-primary-600 uppercase tracking-wider">
                                        Configuration
                                      </th>
                                      <th className="px-3 py-2 text-left text-xs font-medium text-primary-600 uppercase tracking-wider">
                                        Availability Date
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody className="bg-white divide-y divide-gray-200">
                                    {sc.configurations.map((config, configIndex) => (
                                      <tr key={configIndex}>
                                        <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-primary-700">
                                          {config.configuration}
                                        </td>
                                        <td className="px-3 py-2 whitespace-nowrap text-sm text-primary-500">
                                          {config.formattedDate}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}

                          {isExpanded && sc.configurations.length === 0 && (
                            <div className="border-t border-gray-200 p-4 bg-gray-50">
                              <p className="text-sm text-gray-500">
                                No configuration details available for this service component.
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-gray-500 text-sm">No detailed configuration data available.</p>
                )}
              </div>

              <div className="mt-6">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:w-auto sm:text-sm transition-colors"
                  onClick={onClose}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
